import type { Review } from '@/types';

class ReviewService {
  private reviews: Review[] = [];
  private listeners: ((reviews: Review[]) => void)[] = [];

  constructor() {
    this.initializeMockData();
  }

  private initializeMockData() {
    // Mock reviews for demo
    const mockReviews: Review[] = [
      {
        id: '1',
        jobId: '1',
        customerId: 'customer-1',
        customerName: 'John Smith',
        providerId: 'provider-1',
        providerName: 'Elite Plumbing Services',
        rating: 5,
        title: 'Excellent work and professional service',
        comment: 'Mike arrived on time, diagnosed the issue quickly, and fixed it efficiently. Very professional and cleaned up after the work. Highly recommend!',
        photos: ['review1.jpg', 'review2.jpg'],
        response: {
          message: 'Thank you John! It was a pleasure working on your plumbing issue. We appreciate your business and the great review!',
          timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
        },
        helpful: 12,
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
      },
      {
        id: '2',
        jobId: '2',
        customerId: 'customer-2',
        customerName: 'Sarah Johnson',
        providerId: 'provider-1',
        providerName: 'Elite Plumbing Services',
        rating: 4,
        title: 'Good service, minor delay',
        comment: 'Quality work but arrived 30 minutes late. Communication could be better but the final result was good.',
        helpful: 8,
        createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      }
    ];

    this.reviews = mockReviews;
  }

  // Subscribe to review updates
  subscribe(callback: (reviews: Review[]) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners
  private notifyListeners() {
    this.listeners.forEach(listener => listener([...this.reviews]));
  }

  // Submit a new review
  submitReview(
    jobId: string,
    customerId: string,
    customerName: string,
    providerId: string,
    providerName: string,
    rating: number,
    title: string,
    comment: string,
    photos?: string[]
  ): Review {
    const review: Review = {
      id: Date.now().toString() + Math.random().toString(36).substring(2, 11),
      jobId,
      customerId,
      customerName,
      providerId,
      providerName,
      rating,
      title,
      comment,
      photos,
      helpful: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    this.reviews.push(review);
    this.notifyListeners();

    return review;
  }

  // Provider responds to review
  respondToReview(reviewId: string, responseMessage: string): boolean {
    const review = this.reviews.find(r => r.id === reviewId);
    if (review) {
      review.response = {
        message: responseMessage,
        timestamp: new Date()
      };
      review.updatedAt = new Date();
      this.notifyListeners();
      return true;
    }
    return false;
  }

  // Mark review as helpful
  markHelpful(reviewId: string): boolean {
    const review = this.reviews.find(r => r.id === reviewId);
    if (review) {
      review.helpful += 1;
      this.notifyListeners();
      return true;
    }
    return false;
  }

  // Get reviews for a provider
  getProviderReviews(providerId: string): Review[] {
    return this.reviews
      .filter(review => review.providerId === providerId)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  // Get reviews by a customer
  getCustomerReviews(customerId: string): Review[] {
    return this.reviews
      .filter(review => review.customerId === customerId)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  // Get review for a specific job
  getJobReview(jobId: string): Review | null {
    return this.reviews.find(review => review.jobId === jobId) || null;
  }

  // Get provider rating statistics
  getProviderStats(providerId: string): {
    averageRating: number;
    totalReviews: number;
    ratingDistribution: Record<number, number>;
  } {
    const providerReviews = this.getProviderReviews(providerId);
    
    if (providerReviews.length === 0) {
      return {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      };
    }

    const totalRating = providerReviews.reduce((sum, review) => sum + review.rating, 0);
    const averageRating = totalRating / providerReviews.length;

    const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    providerReviews.forEach(review => {
      ratingDistribution[review.rating as keyof typeof ratingDistribution]++;
    });

    return {
      averageRating: Math.round(averageRating * 10) / 10,
      totalReviews: providerReviews.length,
      ratingDistribution
    };
  }

  // Check if customer can review a job
  canReviewJob(jobId: string, customerId: string): boolean {
    // Customer can review if job is completed and they haven't reviewed yet
    const existingReview = this.reviews.find(r => r.jobId === jobId && r.customerId === customerId);
    return !existingReview; // Can review if no existing review
  }

  // Get recent reviews (for homepage/dashboard)
  getRecentReviews(limit: number = 10): Review[] {
    return this.reviews
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, limit);
  }
}

export const reviewService = new ReviewService();